import cls from "classnames"
import { useEffect, useState } from "react"

import { i18n } from "@/i18n"
import { useMapStore, useTransitionStore } from "@/store"
import type { RegionId } from "@/types"
import { Modal } from "../Modal"

import styles from "./index.module.scss"

const regionThumbMap: Record<RegionId, string> = {
    kazdel: require("@/assets/imgs/map/selection/thumb-kazdel.png"),
    sargon: require("@/assets/imgs/map/selection/thumb-sargon.png"),
    sami: require("@/assets/imgs/map/selection/thumb-sami.png"),
    ursus: require("@/assets/imgs/map/selection/thumb-ursus.png"),
    victoria: require("@/assets/imgs/map/selection/thumb-victoria.png"),
    leithanien: require("@/assets/imgs/map/selection/thumb-leithania.png"),
    babel_rhodes: require("@/assets/imgs/map/selection/thumb-rhodes.png"),
}

const regionIconMap: Record<RegionId, string> = {
    kazdel: require("@/assets/imgs/map/selection/icon-kazdel.png"),
    sargon: require("@/assets/imgs/map/selection/icon-sargon.png"),
    sami: require("@/assets/imgs/map/selection/icon-sami.png"),
    ursus: require("@/assets/imgs/map/selection/icon-ursus.png"),
    victoria: require("@/assets/imgs/map/selection/icon-victoria.png"),
    leithanien: require("@/assets/imgs/map/selection/icon-leithania.png"),
    babel_rhodes: require("@/assets/imgs/map/selection/icon-rhodes.png"),
}

interface Props {
    open: boolean
    onCancel: () => void
}

export const MapSelectModal: React.FC<Props> = ({ open, onCancel }) => {
    const mapStore = useMapStore()
    const fadeTransition = useTransitionStore((s) => s.fadeTransition)
    const [selectedRegion, setSelectedRegion] = useState<RegionId | null>(null)
    const regions = mapStore.regions

    useEffect(() => {
        if (open) {
            setSelectedRegion(mapStore.currentRegionId)
        }
    }, [open, mapStore.currentRegionId])

    const handleConfirm = () => {
        if (!selectedRegion) return
        if (selectedRegion !== mapStore.currentRegionId) {
            onCancel()
            fadeTransition(() => {
                mapStore.initMap(selectedRegion)
            }, "map")
        } else {
            onCancel()
        }
    }

    return (
        <Modal
            open={open}
            onCancel={onCancel}
            showCancelBtn={false}
            onOk={handleConfirm}
            contentClassName={styles.mapSelect}
        >
            <div className={styles.grid}>
                {regions.map((region) => {
                    const isSelected = region.id === selectedRegion
                    const isLocked = !region.unlocked

                    return (
                        <div
                            key={region.id}
                            className={cls(styles.item, {
                                [styles.selected]: isSelected,
                                [styles.locked]: isLocked,
                            })}
                            onClick={() => {
                                if (!isLocked) setSelectedRegion(region.id)
                            }}
                        >
                            <div className={styles.thumbWrapper}>
                                <div
                                    className={cls(
                                        styles.outline,
                                        isSelected && styles.active,
                                    )}
                                />
                                <img
                                    className={styles.thumb}
                                    src={regionThumbMap[region.id]}
                                    alt={i18n.get(region.name as never)}
                                    draggable={false}
                                />
                            </div>
                            <img
                                className={styles.icon}
                                src={regionIconMap[region.id]}
                                alt=""
                                draggable={false}
                            />
                        </div>
                    )
                })}
            </div>
            <div className={styles.title} />
        </Modal>
    )
}
